import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ensureCsrf, unwrap } from './client';
import type { components } from './schema';

export type AgentToken = components['schemas']['AgentToken'];
export type AgentTokenList = components['schemas']['AgentTokenList'];
export type AgentTokenCreated = components['schemas']['AgentTokenCreated'];
export type AgentTokenCreateBody = components['schemas']['AgentTokenCreateRequest'];

/** The user's live tokens and OAuth grants, plus the URL an assistant connects to. */
export async function listAgentTokens(): Promise<AgentTokenList> {
  return unwrap(await api.GET('/api/auth/agent-tokens'));
}
/** Creates a token; the response carries the secret exactly once. */
export async function createAgentToken(body: AgentTokenCreateBody): Promise<AgentTokenCreated> {
  await ensureCsrf();
  return unwrap(await api.POST('/api/auth/agent-tokens', { body }));
}
export async function revokeAgentToken(id: string): Promise<void> {
  await ensureCsrf();
  unwrap(await api.DELETE('/api/auth/agent-tokens/{public_id}', { params: { path: { public_id: id } } }));
}

export function useAgentTokens() {
  return useQuery({ queryKey: ['agent-tokens'], queryFn: listAgentTokens });
}
export function useCreateAgentToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createAgentToken,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['agent-tokens'] }),
  });
}
export function useRevokeAgentToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: revokeAgentToken,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['agent-tokens'] }),
  });
}
